import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { KeyRound, AlertTriangle, Loader2, ArrowRight } from 'lucide-react';
import { verifyAccessCode } from '@/lib/access-code-api';

interface AccessCodeGateProps {
  onVerified: (code: string) => void;
}

export function AccessCodeGate({ onVerified }: AccessCodeGateProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await verifyAccessCode(code.trim().toLowerCase());

      if (result.success) {
        onVerified(code.trim().toLowerCase());
      } else {
        setError(result.error || t('login.invalidCode'));
      }
    } catch {
      setError(t('login.unableToVerifyCode'));
    }

    setIsLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex items-start space-x-3">
          <KeyRound className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-gray-700">
            <p className="font-semibold mb-1">{t('login.studyAccessRequiredTitle')}</p>
            <p>{t('login.studyAccessRequiredBody')}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Input
            type="text"
            placeholder={t('login.enterCodePlaceholder')}
            value={code}
            onChange={(e) => setCode(e.target.value.toLowerCase())}
            maxLength={5}
            className="text-center text-2xl tracking-widest font-mono h-14"
            autoFocus
            autoComplete="off"
            disabled={isLoading}
          />
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button
          type="submit"
          disabled={!code.trim() || isLoading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t('login.verifying')}
            </>
          ) : (
            <>
              <ArrowRight className="h-5 w-5 mr-2" />
              {t('login.continue')}
            </>
          )}
        </Button>
      </form>
    </div>
  );
}
